import { FavoriteFillIcon, FavoriteOutlineIcon } from "@kds-web/icons";
import { showSnackbar } from "@kds-web/snackbar";
import { actions } from "astro:actions";
import { useEffect, useState } from "react";
import type { JSX } from "react/jsx-runtime";
import type { FeedItem } from "../../../../actions/feed/feed.ts";
import type { EditWatchlistResponse } from "../../../../actions/watchlist/watchlist.ts";

import { trackGAEvent } from "../../../../utils/google-analytics/tracking.ts";
import FeedbackSurvey from "./FeedbackSurvey.tsx";
import { formatPrice } from "./utils.ts";

interface FeedItemProps {
  feedItem: FeedItem;
  index?: number;
  originForTracking: string;
}

function FeedItemTile({
  feedItem,
  index,
  originForTracking,
}: FeedItemProps): JSX.Element {
  const [onWatchlist, setOnWatchlist] = useState(feedItem.onWatchlist);
  const [showFeedbackSurvey, setShowFeedbackSurvey] = useState(false);

  useEffect(() => {
    if (!document.cookie.includes("homepageSurveyClicked=1") && index === 19) {
      setShowFeedbackSurvey(true);
    }
  }, [index]);

  async function editWatchlist(action: string): Promise<EditWatchlistResponse> {
    const response = await actions.homepageWeb.watchlist.editWatchlist({
      adId: feedItem.adId,
      action,
    });

    return response.data as EditWatchlistResponse;
  }

  async function handleWatchlistClick(): Promise<void> {
    if (onWatchlist) {
      await removeFromWatchlist();
    } else {
      await addToWatchlist();
    }
  }

  async function addToWatchlist(): Promise<void> {
    trackEditWatchlistEvent("WatchlistAddAttempt");
    await editWatchlist("store").then((response) => {
      if (response.status === "OK") {
        setOnWatchlist(true);
        trackEditWatchlistEvent("WatchlistAddSuccess");
      } else if (response.status === "ERROR") {
        handleFullWatchlist();
        setOnWatchlist(false);
      } else if (response.status === "NOT_AUTHENTICATED") {
        redirectToLogin(response.message);
      }
    });
  }

  async function removeFromWatchlist(): Promise<void> {
    trackEditWatchlistEvent("WatchlistRemoveAttempt");
    editWatchlist("del").then((response) => {
      if (response.status === "OK") {
        setOnWatchlist(false);
        trackEditWatchlistEvent("WatchlistRemoveSuccess");
      }
    });
  }

  function handleFullWatchlist(): void {
    showSnackbar({
      message:
        "Deine Merkliste ist voll, sie hat bereits 100 Einträge. Bitte lösche einige Einträge",
      isClosable: true,
    });
  }

  function redirectToLogin(redirectUrl: string): void {
    window.location.href = redirectUrl;
  }

  function trackEditWatchlistEvent(eventAction: string): void {
    trackGAEvent({
      eventCategory: originForTracking,
      eventAction,
    });
  }

  function trackClickOnFeedItem(feedItem: FeedItem): void {
    if (feedItem.sustainable) {
      trackGAEvent({
        eventCategory: originForTracking,
        eventAction: "ClickSustainableAd",
        eventLabel: feedItem.adId.toString(),
      });
    }
  }

  const itemPrice = formatPrice(feedItem.price);

  return showFeedbackSurvey ? (
    <FeedbackSurvey originForTracking={originForTracking} />
  ) : (
    <>
      <a
        id={`feedItem-${feedItem.adId}`}
        href={feedItem.viewAdSeoLink}
        onClick={(): void => trackClickOnFeedItem(feedItem)}
        className="w-[165px]"
      >
        <div className="h-[140px]">
          <img
            className="size-full object-cover"
            alt=""
            src={feedItem.imageUrl}
            loading="lazy"
          />
        </div>
        <div className="relative flex flex-col p-xsmall text-onSurface">
          <h3 className="mb-[6px] truncate text-bodySmallStrong text-onSurfaceSubdued">
            {feedItem.title}
          </h3>
          <div className="truncate pb-xsmall text-bodySmall font-regular text-onSurfaceNonessential">
            {feedItem.location}
          </div>
          {feedItem.sustainable && (
            <span className="box-border inline-block w-fit rounded-xsmall border	border-surfaceSubdued bg-surfaceSubdued px-xxsmall text-bodySmall font-regular text-onSurfaceSubdued">
              Nachhaltigkeitsfokus
            </span>
          )}
          {feedItem.priceEnabled && (
            <div className="absolute right-[-6px] top-[-34px] inline-block max-w-[100px] cursor-pointer bg-primary px-xxsmall py-xxxsmall text-right text-bodySmallStrong text-onPrimary">
              <span>{itemPrice}</span>
              <span className="absolute -bottom-xxsmall right-xxxsmall rotate-45 border-4 border-transparent border-r-interactive" />
            </div>
          )}
        </div>
      </a>
      <button
        type="button"
        className="absolute right-xsmall top-xsmall flex rounded-full bg-surface bg-opacity-high"
        onClick={handleWatchlistClick}
        aria-label="Zur Merkliste hinzufügen"
        aria-describedby={`feedItem-${feedItem.adId}`}
      >
        <div className="flex w-fit items-center p-xxsmall text-bodySmall font-regular">
          {onWatchlist ? (
            <FavoriteFillIcon size="medium" className="text-accent" />
          ) : (
            <FavoriteOutlineIcon size="medium" />
          )}
        </div>
      </button>
    </>
  );
}

export default FeedItemTile;
